import { townColorHex as hex, type TownPropPalette } from './townPropTemplates';
import { drawMasonryBlocks, drawTimberBoards } from './propDrawingPrimitives';

export type FacadePanelTemplate = 'plaster_wall_bay' | 'timber_wall_bay' | 'arched_entry_door' | 'plank_entry_door';

function bayFrame(p: TownPropPalette): string {
  const timber = { ...p, primary: p.wood };
  return `<g data-part="bay-frame">
    ${drawTimberBoards({ x: 0, y: 0, width: 160, height: 14 }, timber, { boards: 1 })}
    ${drawTimberBoards({ x: 0, y: 14, width: 10, height: 146 }, timber, { axis: 'vertical', boards: 1 })}
    ${drawTimberBoards({ x: 150, y: 14, width: 10, height: 146 }, timber, { axis: 'vertical', boards: 1 })}
    <path d="M10 14H150V18H10Z" fill="${hex(p.shade)}" opacity="0.6" />
    <path d="M2 2H158 M2 15V156 M157 15V156" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.2" />
    <g fill="${hex(p.iron)}"><circle cx="5" cy="7" r="1.5" /><circle cx="155" cy="7" r="1.5" />
      <circle cx="5" cy="149" r="1.4" /><circle cx="155" cy="149" r="1.4" /></g>
  </g>
  <g data-part="bay-stone-base">
    ${drawMasonryBlocks({ x: 0, y: 160, width: 160, height: 20 }, p, { courses: 2, columns: 5 })}
    <rect x="0" y="159" width="160" height="3" fill="${hex(p.stone)}" />
  </g>`;
}

/** 160×180, roofless. The solid backing includes the entire declared mounting region. */
function plasterBay(p: TownPropPalette): string {
  return `<g data-part="plaster-wall-host">
    <rect x="0" y="0" width="160" height="180" fill="${hex(p.primary)}" />
    <path d="M10 18H20L17 62L20 108L15 158H10Z M143 18H150V159H138L142 120L140 75Z"
      fill="${hex(p.shade)}" opacity="0.18" />
    <g data-part="plaster-weathering" fill="${hex(p.stone)}" opacity="0.32">
      <path d="M15 143L23 141L26 147L23 155H13Z M132 148L143 146L148 152L145 159H131Z M17 34L23 30L25 35L21 38Z" />
    </g>
    <path d="M137 52L134 59L140 68L137 77 M140 68L145 70 M29 151L31 143L28 137"
      fill="none" stroke="${hex(p.shade)}" stroke-width="0.8" opacity="0.35" />
    ${bayFrame(p)}
  </g>`;
}

/** 160×180, roofless. Board seams are painted over an opaque backing, never cutouts. */
function timberBay(p: TownPropPalette): string {
  return `<g data-part="timber-wall-host">
    <rect x="0" y="0" width="160" height="180" fill="${hex(p.shade)}" />
    ${drawTimberBoards({ x: 10, y: 14, width: 140, height: 146 }, p, { axis: 'vertical', boards: 9 })}
    <path d="M10 22H150V28H10Z M10 145H150V151H10Z" fill="${hex(p.wood)}" />
    <path d="M12 23H148 M12 146H148" stroke="${hex(p.woodEdge)}" stroke-width="1" />
    <g fill="${hex(p.iron)}">${[18, 49, 80, 111, 142].map(x =>
  `<circle cx="${x}" cy="25" r="1.1" /><circle cx="${x}" cy="148" r="1.1" />`).join('')}</g>
    ${bayFrame(p)}
  </g>`;
}

function hinge(x: number, y: number, p: TownPropPalette): string {
  return `<g data-part="door-hinge">
    <path d="M${x} ${y - 3}H${x + 17}L${x + 21} ${y}L${x + 17} ${y + 3}H${x}Z" fill="${hex(p.iron)}" />
    <rect x="${x}" y="${y - 5}" width="3" height="10" rx="0.8" fill="${hex(p.iron)}" />
    <circle cx="${x + 6}" cy="${y}" r="0.9" fill="${hex(p.stone)}" />
    <circle cx="${x + 15}" cy="${y}" r="0.9" fill="${hex(p.stone)}" />
  </g>`;
}

function ringHandle(x: number, y: number, p: TownPropPalette): string {
  return `<g data-part="door-handle">
    <path d="M${x - 3} ${y - 7}L${x} ${y - 9}L${x + 3} ${y - 7}V${y + 7}L${x} ${y + 9}L${x - 3} ${y + 7}Z" fill="${hex(p.iron)}" />
    <circle cx="${x}" cy="${y - 3}" r="1.6" fill="${hex(p.stone)}" />
    <ellipse cx="${x}" cy="${y + 2}" rx="3.2" ry="4" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.7" />
  </g>`;
}

/** 64×116, decorative wall fixture. Stone arch, glazed tracery and hardwood leaf. */
function archedDoor(p: TownPropPalette): string {
  const doorWood = { ...p, primary: p.wood, wood: p.shade };
  return `<g data-part="arched-door-surround">
    <path d="M5 110V38C5 20 17 4 32 3C47 4 59 20 59 38V110Z" fill="${hex(p.stone)}" />
    <path d="M12 110V39Q12 22 32 14Q52 22 52 39V110Z" fill="${hex(p.iron)}" />
    <path d="M15 110V40Q15 25 32 18Q49 25 49 40V110Z" fill="${hex(p.wood)}" />
    ${drawMasonryBlocks({ x: 5, y: 43, width: 7, height: 65 }, p, { courses: 4, columns: 1 })}
    ${drawMasonryBlocks({ x: 52, y: 43, width: 7, height: 65 }, p, { courses: 4, columns: 1 })}
    <path d="M6 35L12 36 M10 22L17 27 M18 11L23 19 M46 11L41 19 M54 22L47 27 M58 35L52 36"
      stroke="${hex(p.shade)}" stroke-width="1.3" />
    <path d="M27 5H37L35 17H29Z" fill="${hex(p.woodEdge)}" />
    <path d="M7 39C7 23 18 8 25 8 M39 8C48 12 57 26 57 39" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1" />
  </g>
  <g data-part="arched-door-leaf">
    ${drawTimberBoards({ x: 15, y: 44, width: 34, height: 66 }, doorWood, { axis: 'vertical', boards: 5 })}
    <path d="M17 43V39Q18 28 32 21Q46 28 47 39V43Z" fill="${hex(p.glass)}" />
    <path data-part="door-tracery" d="M32 22V43 M17 42Q21 31 27 29Q32 34 32 42Q32 34 37 29Q43 31 47 42
      M19 39Q32 27 45 39" fill="none" stroke="${hex(p.wood)}" stroke-width="1.8" />
    <path d="M17 45H47 M17 104H47" stroke="${hex(p.woodEdge)}" stroke-width="1.4" />
    ${hinge(13, 57, p)}${hinge(13, 97, p)}${ringHandle(42, 76, p)}
  </g>
  <g data-part="door-threshold"><path d="M5 108H59L62 112V116H2V112Z" fill="${hex(p.stone)}" />
    <path d="M2 114H62V116H2Z" fill="${hex(p.shade)}" />
    <path d="M6 111H58" stroke="${hex(p.woodEdge)}" stroke-width="1.2" /></g>`;
}

/** 64×112, decorative wall fixture. Rectangular frame and a braced plank leaf. */
function plankDoor(p: TownPropPalette): string {
  const timber = { ...p, primary: p.wood };
  return `<g data-part="plank-door-frame">
    <rect x="4" y="8" width="56" height="100" fill="${hex(p.iron)}" />
    ${drawTimberBoards({ x: 4, y: 8, width: 7, height: 99 }, timber, { axis: 'vertical', boards: 1 })}
    ${drawTimberBoards({ x: 53, y: 8, width: 7, height: 99 }, timber, { axis: 'vertical', boards: 1 })}
    ${drawTimberBoards({ x: 2, y: 2, width: 60, height: 10 }, timber, { boards: 1 })}
    <path d="M3 3H61 M6 14V104 M58 14V104" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.2" />
  </g>
  <g data-part="plank-door-leaf">
    ${drawTimberBoards({ x: 13, y: 15, width: 38, height: 91 }, p, { axis: 'vertical', boards: 5 })}
    <path d="M13 26H51V33H13Z M13 85H51V92H13Z" fill="${hex(p.wood)}" />
    <path data-part="door-diagonal" d="M16 85L44 33H50L22 85Z" fill="${hex(p.woodEdge)}" />
    <path d="M21 79L45 36" stroke="${hex(p.wood)}" stroke-width="1.1" />
    ${hinge(9, 30, p)}${hinge(9, 89, p)}${ringHandle(44, 61, p)}
    <path d="M34 98L44 98" stroke="${hex(p.shade)}" stroke-width="1" />
  </g>
  <g data-part="door-threshold"><path d="M4 105H60L62 108V112H2V108Z" fill="${hex(p.stone)}" />
    <path d="M2 110H62V112H2Z" fill="${hex(p.shade)}" />
    <path d="M6 107H58" stroke="${hex(p.woodEdge)}" stroke-width="1.1" /></g>`;
}

/** Art only: wall mounting, fixed render bands and gameplay stay outside these builders. */
export function buildFacadePanelBody(template: FacadePanelTemplate, p: TownPropPalette): string {
  switch (template) {
  case 'plaster_wall_bay': return plasterBay(p);
  case 'timber_wall_bay': return timberBay(p);
  case 'arched_entry_door': return archedDoor(p);
  case 'plank_entry_door': return plankDoor(p);
  }
}
